// Browser-side session persistence.
//
// Stores (DB `tro-voice-studio`, Version 3 seit 2026-09-24):
//   sessions     — ein Record pro persistKey, NUR Metadaten (Blöcke, Takes
//                  ohne Bytes, Versionen, Videodauern) — Audit M9
//   take-audio   — die Bytes: ein Eintrag pro Take (`<persistKey>:tts:<id>`,
//                  `<persistKey>:sts:<id>:out|in`), genau einmal geschrieben
//   sfx-beds     — Music/SFX-Beds pro (persistKey, assetIdx)
//   video-blobs  — Video-Blobs (Legacy-Store, wird nicht mehr beschrieben)
//
// Session-Record und Audio werden in EINER Transaktion geschrieben, damit
// kein Zwischenstand entsteht (Record verweist auf Bytes, die es nicht
// gibt). Alt-Records mit eingebetteten Bytes (Version ≤ 2) wandern beim
// ersten Laden verlustfrei in den neuen Store.

import type { SessionState, VideoState } from '@shared/types';
import {
  hasEmbeddedAudio,
  mergeSessionAudio,
  orphanAudioKeys,
  splitSessionAudio,
  type AudioMap,
} from './sessionAudio';

const DB_NAME = 'tro-voice-studio';
const DB_VERSION = 3;
const STORE_SESSIONS = 'sessions';
const STORE_VIDEO_BLOBS = 'video-blobs';
const STORE_SFX_BEDS = 'sfx-beds';
const STORE_TAKE_AUDIO = 'take-audio';

/** Record-Format mit ausgelagertem Audio (M9). Fehlt → Alt-Record. */
export const SESSION_RECORD_FORMAT = 3;

type OpenedDb = IDBDatabase;

/** Error thrown when an IDB upgrade is blocked by another open tab/window
 *  holding the database at an older version. useSessionPersistence zeigt
 *  ihn in der Fehleransicht (statt eines generischen „storage error"). */
export class IDBUpgradeBlockedError extends Error {
  name = 'IDBUpgradeBlockedError';
  constructor() {
    super('Storage upgrade pending — please close other Voice Studio tabs and reload.');
  }
}

/** Cached connection so we don't re-open per call. Re-opening on every IDB
 *  helper invocation would also block the upgrade path forever once a single
 *  connection landed at an older version. */
let _dbPromise: Promise<OpenedDb> | null = null;

function openDb(): Promise<OpenedDb> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise<OpenedDb>((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      // Some Safari/private-mode setups throw synchronously on open.
      reject(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'toolId' });
      }
      if (!db.objectStoreNames.contains(STORE_VIDEO_BLOBS)) {
        db.createObjectStore(STORE_VIDEO_BLOBS);
      }
      if (!db.objectStoreNames.contains(STORE_SFX_BEDS)) {
        db.createObjectStore(STORE_SFX_BEDS);
      }
      if (!db.objectStoreNames.contains(STORE_TAKE_AUDIO)) {
        db.createObjectStore(STORE_TAKE_AUDIO);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // If another tab later opens a NEWER version, drop the cached promise
      // so the next call re-opens (and that newer tab can upgrade).
      db.onversionchange = () => {
        try { db.close(); } catch { /* ignore */ }
        _dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      _dbPromise = null;
      reject(req.error ?? new Error('IndexedDB open failed'));
    };
    req.onblocked = () => {
      // Another tab holds the DB open at an older version — the upgrade
      // can't proceed until that tab closes the connection. Reject with a
      // specific error class so callers can surface an actionable message.
      _dbPromise = null;
      reject(new IDBUpgradeBlockedError());
    };
  });
  return _dbPromise;
}

/** Format an IDB / DOMException error for user display. Surfaces the
 *  underlying error name (`QuotaExceededError`, `InvalidStateError`,
 *  `DataCloneError`, etc.) so the user can see what actually failed
 *  instead of a generic "storage error". */
export function formatIDBError(err: unknown): string {
  if (err instanceof IDBUpgradeBlockedError) return err.message;
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) return `${err.name}: ${err.message}`;
  if (err instanceof Error) {
    const name = err.name && err.name !== 'Error' ? `${err.name}: ` : '';
    return `${name}${err.message || 'Unknown storage error'}`;
  }
  return 'Unknown storage error';
}

/** Key range covering every `${prefix}:<rest>` key. ':' is 0x3A; ';' is the
 *  next code point, so [`${prefix}:`, `${prefix};`) bounds the prefix. */
function prefixRange(prefix: string): IDBKeyRange {
  return IDBKeyRange.bound(`${prefix}:`, `${prefix};`, false, true);
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IDB tx failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IDB tx aborted'));
  });
}

function reqResult<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB request failed'));
  });
}

export interface StoredSession {
  toolId: string;
  savedAt: string;
  /** SESSION_RECORD_FORMAT — Takes ohne Bytes. Fehlt bei Alt-Records. */
  format?: number;
  session: SessionState;
  video: Omit<VideoState, 'blobs' | 'sources'>; // blobs stored separately; sources are blob:-URLs (ephemeral)
  /** Combined asset-id order (config.assets + customAssets) at save time.
   *  Restore diffs it against the fresh config to re-key the index-based
   *  session maps when the admin changed assets mid-session (audit high).
   *  Absent on pre-2026-07-23 records → restore keeps indices as-is. */
  assetIdOrder?: string[];
}

async function readAudio(db: IDBDatabase, persistKey: string): Promise<AudioMap> {
  const tx = db.transaction(STORE_TAKE_AUDIO, 'readonly');
  const store = tx.objectStore(STORE_TAKE_AUDIO);
  const range = prefixRange(persistKey);
  const [keys, vals] = await Promise.all([
    reqResult(store.getAllKeys(range)),
    reqResult(store.getAll(range)),
  ]);
  const out: AudioMap = new Map();
  (keys as string[]).forEach((k, i) => {
    const v = vals[i];
    if (v instanceof ArrayBuffer) out.set(k.slice(persistKey.length + 1), v);
  });
  return out;
}

/** Session-Record inkl. Audio laden. Alt-Records (Bytes im Record) werden
 *  dabei einmalig ins neue Format überführt — in einer Transaktion, ohne
 *  Datenverlust. `missing` nennt Audio-Schlüssel, die der Record
 *  referenziert, die aber nicht (mehr) im Store liegen; die betroffenen
 *  Takes sind aus `session` bereits entfernt. */
export async function loadSession(
  persistKey: string,
): Promise<{ record: StoredSession; session: SessionState; missing: string[] } | null> {
  const db = await openDb();
  const record = (await reqResult(
    db.transaction(STORE_SESSIONS, 'readonly').objectStore(STORE_SESSIONS).get(persistKey),
  )) as StoredSession | undefined;
  if (!record) return null;

  if (record.format !== SESSION_RECORD_FORMAT || hasEmbeddedAudio(record.session)) {
    // Migration: Bytes aus dem Record in den Audio-Store, Record neu
    // schreiben. Der Aufrufer bekommt den Stand mit Bytes zurück.
    const { session: stripped, audio } = splitSessionAudio(record.session);
    const migrated: StoredSession = { ...record, format: SESSION_RECORD_FORMAT, session: stripped };
    const tx = db.transaction([STORE_SESSIONS, STORE_TAKE_AUDIO], 'readwrite');
    const audioStore = tx.objectStore(STORE_TAKE_AUDIO);
    for (const [k, buf] of audio) audioStore.put(buf, `${persistKey}:${k}`);
    tx.objectStore(STORE_SESSIONS).put(migrated);
    await txDone(tx);
    return { record: migrated, session: record.session, missing: [] };
  }

  const audio = await readAudio(db, persistKey);
  const { session, missing } = mergeSessionAudio(record.session, audio);
  return { record, session, missing };
}

/** Record + neue Audio-Bytes schreiben und verwaiste Bytes löschen — eine
 *  Transaktion über beide Stores. `audio` sind ALLE Bytes der Session; nur
 *  Schlüssel, die noch fehlen, werden geschrieben (die Bytes eines Takes
 *  ändern sich nie). */
export async function saveSessionRecord(
  record: StoredSession,
  audio: AudioMap,
): Promise<void> {
  const db = await openDb();
  const persistKey = record.toolId;
  const tx = db.transaction([STORE_SESSIONS, STORE_TAKE_AUDIO], 'readwrite');
  const audioStore = tx.objectStore(STORE_TAKE_AUDIO);
  const done = txDone(tx);
  const existing = (await reqResult(audioStore.getAllKeys(prefixRange(persistKey)))) as string[];
  const have = new Set(existing.map((k) => k.slice(persistKey.length + 1)));
  const live = new Set(audio.keys());
  for (const [k, buf] of audio) {
    if (!have.has(k)) audioStore.put(buf, `${persistKey}:${k}`);
  }
  for (const k of orphanAudioKeys(have, live)) audioStore.delete(`${persistKey}:${k}`);
  tx.objectStore(STORE_SESSIONS).put({ ...record, format: SESSION_RECORD_FORMAT });
  await done;
}

/** Session-Record UND alle Audio-Bytes des persistKey löschen (Start Fresh). */
export async function clearSession(persistKey: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORE_SESSIONS, STORE_TAKE_AUDIO], 'readwrite');
  tx.objectStore(STORE_SESSIONS).delete(persistKey);
  tx.objectStore(STORE_TAKE_AUDIO).delete(prefixRange(persistKey));
  await txDone(tx);
}

// SFX bed records — one per (toolId, assetIdx), holding ALL beds of that
// asset (max 2 tracks) plus the metadata needed to rebuild the SfxTrack
// rows without re-decoding: name, duration, placement, mime type. The
// buffer is the raw ArrayBuffer (structured-cloneable); the blob: URL is
// rebuilt on hydrate. Wired by useSessionPersistence (mirror-on-change +
// restore-on-continue) — before 2026-07-23 these helpers were dead code
// and beds silently vanished on reload (audit high).

export interface StoredSfxBedEntry {
  id: string;
  name: string;
  duration: number;
  buffer: ArrayBuffer;
  mimeType: string;
  /** place-mode: clip start on the asset timeline (s). */
  offset?: number;
  /** window-mode: source window start (s). */
  sourceOffset?: number;
}

export interface StoredSfxBeds {
  beds: StoredSfxBedEntry[];
  savedAt: string; // ISO
}

function sfxBedKey(toolId: string, assetIdx: number): string {
  return `${toolId}:${assetIdx}`;
}

export async function putSfxBeds(
  toolId: string,
  assetIdx: number,
  record: StoredSfxBeds
): Promise<void> {
  const db = await openDb();
  // Defensive: if the SFX-bed store didn't get created (e.g. an older
  // connection somehow lingered), surface that explicitly instead of letting
  // `transaction()` throw a generic NotFoundError.
  if (!db.objectStoreNames.contains(STORE_SFX_BEDS)) {
    throw new Error(
      `SFX-bed store missing (db v${db.version}). Please reload the page.`,
    );
  }
  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_SFX_BEDS, 'readwrite');
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    let req: IDBRequest;
    try {
      req = tx.objectStore(STORE_SFX_BEDS).put(record, sfxBedKey(toolId, assetIdx));
    } catch (err) {
      // Synchronous structured-clone failures (e.g. trying to store a
      // non-cloneable object) land here.
      reject(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    // Wire request-level error so quota / data-clone failures don't slip
    // through silently — `tx.onerror` fires AFTER `req.onerror`, but only
    // if the request error isn't preventDefault'd. Both paths reject.
    req.onerror = () => reject(req.error ?? new Error('IDB put failed'));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? req.error ?? new Error('IDB tx failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IDB tx aborted'));
  });
}

/** Load every persisted bed record for the tool, keyed by assetIdx. Used
 *  by the session-restore path (Continue). */
export async function getAllSfxBeds(
  toolId: string
): Promise<Record<number, StoredSfxBeds>> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SFX_BEDS, 'readonly');
    const store = tx.objectStore(STORE_SFX_BEDS);
    const range = prefixRange(toolId);
    const keysReq = store.getAllKeys(range);
    const valsReq = store.getAll(range);
    tx.oncomplete = () => {
      const out: Record<number, StoredSfxBeds> = {};
      const keys = (keysReq.result ?? []) as string[];
      const vals = (valsReq.result ?? []) as StoredSfxBeds[];
      keys.forEach((key, i) => {
        const idx = Number(key.slice(toolId.length + 1));
        if (Number.isFinite(idx) && vals[i]) out[idx] = vals[i];
      });
      resolve(out);
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteSfxBeds(
  toolId: string,
  assetIdx: number
): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SFX_BEDS, 'readwrite');
    tx.objectStore(STORE_SFX_BEDS).delete(sfxBedKey(toolId, assetIdx));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Wholesale delete of every bed record for the tool — Start-Fresh path,
 *  alongside clearSession(). */
export async function deleteAllSfxBeds(toolId: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SFX_BEDS, 'readwrite');
    tx.objectStore(STORE_SFX_BEDS).delete(prefixRange(toolId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
